/** Fixed overlay coordinates for table action menus (last column / sticky right). */
export interface ActionDropdownOverlayPosition {
  top: number;
  left: number | null;
  right: number | null;
  openAbove: boolean;
}

const VIEWPORT_PADDING = 12;
const GAP = 6;
const ITEM_HEIGHT = 40;
const PANEL_PADDING = 12;

/**
 * Position a fixed action dropdown anchored to a table cell button.
 * Menu width is `max-content` (see styles/action-dropdown.scss); only top/left/right are set here.
 */
export function computeActionDropdownOverlayPosition(
  anchorRect: DOMRect,
  options?: {
    minWidth?: number;
    itemCount?: number;
    /** When false, align menu left edge to button (default: true for right column). */
    alignEnd?: boolean;
  },
): ActionDropdownOverlayPosition {
  const itemCount = Math.max(1, options?.itemCount ?? 4);
  const alignEnd = options?.alignEnd ?? true;
  const estimatedHeight = itemCount * ITEM_HEIGHT + PANEL_PADDING;

  const spaceBelow = window.innerHeight - anchorRect.bottom - GAP - VIEWPORT_PADDING;
  const spaceAbove = anchorRect.top - GAP - VIEWPORT_PADDING;
  const openAbove =
    spaceBelow < estimatedHeight && spaceAbove > spaceBelow;

  const top = openAbove
    ? Math.max(VIEWPORT_PADDING, anchorRect.top - estimatedHeight - GAP)
    : anchorRect.bottom + GAP;

  if (alignEnd) {
    return {
      top,
      left: null,
      right: window.innerWidth - anchorRect.right,
      openAbove,
    };
  }

  let left = anchorRect.left;
  const maxLeft = window.innerWidth - VIEWPORT_PADDING - (options?.minWidth ?? 80);
  left = Math.max(VIEWPORT_PADDING, Math.min(left, maxLeft));

  return { top, left, right: null, openAbove };
}
